// src/oura/components/newscan/AnchorTownCard.tsx
//
// The Business step's card for an outlet named after its own town ("Subang
// Jaya" in Subang Jaya). The city-wide questions already ask about the town,
// so the outlet's own questions would repeat them (city_rule
// anchor_is_not_a_town). Asked before drafting, like "<Area> is not on the
// website", and answered in place:
//
//   Use this area           the outlet takes the sub-area typed in the field
//                           (SS15) and keeps the town (Subang Jaya)
//   It has no smaller area  the outlet keeps its name and its city-wide
//                           questions ask the wider ring customers type: KL
//                           for PJ; KL or Klang Valley for Subang Jaya and
//                           Shah Alam, picked on a second line
//
// Enter in the field is "Use this area". Every answer is a Business write, so
// the draft's Business patch carries it.

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "@oura/lib/cn";
import { TYPE } from "@oura/ui";
import { inputClass } from "@oura/onboarding/messageText";
import { REGION, SUB_AREA_EXAMPLES, sameTown, widerRing } from "@oura/newscan/towns";
import { DecisionCard } from "./DecisionCard";

const COPY = {
    title: (area: string) => `${area} is a town, not the outlet's area`,
    detail: (area: string) =>
        `City-wide questions already ask about ${area}. Name the part of ${area} the outlet is in, so its own questions are about its doorstep.`,
    field: (area: string) => `Area within ${area}`,
    examples: (list: string[]) =>
        list.length ? `For example ${list.length === 1 ? list[0] : `${list.slice(0, -1).join(", ")} or ${list[list.length - 1]}`}.` : "The part of town customers name for it.",
    placeholder: (list: string[]) => (list[0] ? `e.g. ${list[0]}` : ""),
    use: "Use this area",
    none: "It has no smaller area",
    typeFirst: "Type the area first.",
    sameAgain: (area: string) => `That is ${area} again. Name a smaller part of it.`,
    taken: (name: string) => `${name} is already in the list.`,
    ringDetail: (area: string) => `The outlet stays ${area}. Pick what customers type for the wider area: the city-wide questions ask it.`,
    ringUse: (ring: string) => `Use ${ring}`,
    ringNote: `${REGION}: only if customers type it.`,
    back: "Back",
} as const;

export interface AnchorTownCardProps {
    /** The outlet's name as written ("Subang Jaya"). */
    area: string;
    /** The town it stands for, as city_rule writes it ("Subang Jaya", "PJ"). */
    town: string;
    /** The other outlets' names, for the duplicate check. */
    others: string[];
    disabled?: boolean;
    /** "Use this area": the sub-area, trimmed. */
    onUseArea(subArea: string): void;
    /** "It has no smaller area": the wider ring picked, or null when the town has none. */
    onNoSmallerArea(ring: string | null): void;
}

export function AnchorTownCard({ area, town, others, disabled, onUseArea, onNoSmallerArea }: AnchorTownCardProps) {
    const fieldId = useId();
    const hintId = `${fieldId}-hint`;
    const [text, setText] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [choosing, setChoosing] = useState(false);
    const wrap = useRef<HTMLDivElement>(null);
    const examples = SUB_AREA_EXAMPLES[town] ?? [];
    const rings = widerRing(town);

    // The second line opens on a click: focus its first choice; Back returns to the field.
    useEffect(() => {
        if (choosing) wrap.current?.querySelector<HTMLElement>("button")?.focus();
    }, [choosing]);

    // A refusal moves focus to the field, so a click on "Use this area" lands where the fix is typed.
    const fail = (message: string) => {
        setError(message);
        document.getElementById(fieldId)?.focus();
    };

    const submit = () => {
        const name = text.trim().replace(/\s+/g, " ");
        if (!name) return fail(COPY.typeFirst);
        if (sameTown(name, town) || name.toLowerCase() === area.trim().toLowerCase()) return fail(COPY.sameAgain(area));
        if (others.some((o) => o.trim().toLowerCase() === name.toLowerCase())) return fail(COPY.taken(name));
        setError(null);
        onUseArea(name);
    };

    const none = () => {
        if (rings.length > 1) setChoosing(true);
        else onNoSmallerArea(rings[0] ?? null);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.nativeEvent.isComposing || e.key !== "Enter") return;
        e.preventDefault();
        submit();
    };

    if (choosing) {
        return (
            <div ref={wrap} data-anchor-card="">
                <DecisionCard
                    severity="decide"
                    title={COPY.title(area)}
                    detail={COPY.ringDetail(area)}
                    disabled={disabled}
                    source={rings.includes(REGION) ? COPY.ringNote : undefined}
                    actions={[
                        ...rings.map((ring, i) => ({
                            key: `ring:${ring}`,
                            label: COPY.ringUse(ring),
                            kind: i === 0 ? ("primary" as const) : ("secondary" as const),
                            onClick: () => onNoSmallerArea(ring),
                        })),
                        {
                            key: "back",
                            label: COPY.back,
                            kind: "text" as const,
                            onClick: () => {
                                setChoosing(false);
                                requestAnimationFrame(() => document.getElementById(fieldId)?.focus());
                            },
                        },
                    ]}
                />
            </div>
        );
    }

    return (
        <div ref={wrap} data-anchor-card="">
            <DecisionCard
                severity="decide"
                title={COPY.title(area)}
                detail={COPY.detail(area)}
                disabled={disabled}
                actions={[
                    { key: "use", label: COPY.use, kind: "primary", onClick: submit },
                    { key: "none", label: COPY.none, kind: "secondary", onClick: none },
                ]}
            >
                <div className="max-w-sm">
                    <label htmlFor={fieldId} className={cn(TYPE.lab, "mb-1.5 block")}>
                        {COPY.field(area)}
                    </label>
                    <input
                        id={fieldId}
                        type="text"
                        value={text}
                        disabled={disabled}
                        autoComplete="off"
                        enterKeyHint="done"
                        placeholder={COPY.placeholder(examples)}
                        aria-describedby={hintId}
                        aria-invalid={error ? true : undefined}
                        onChange={(e) => {
                            setText(e.target.value);
                            if (error) setError(null);
                        }}
                        onKeyDown={onKeyDown}
                        // 16px on a phone so iOS does not zoom into the field.
                        className={cn(inputClass(error), "max-sm:text-base")}
                    />
                    <p id={hintId} role={error ? "alert" : undefined} className={cn(TYPE.tc, "m-0 mt-1.5", error ? "font-medium text-rose-600" : "text-gray-500")}>
                        {error ?? COPY.examples(examples)}
                    </p>
                </div>
            </DecisionCard>
        </div>
    );
}
